import type { ContactUsSection } from "@/content/types";

const SERVICE_CATEGORIES: ContactUsSection[] = [
  {
    key: "accounting",
    headline: "Talk With An Accounting Expert",
    header: "Clean Books, Clear Reporting, Confident Decisions",
    description: [
      "From day-to-day bookkeeping to month-end close, we help you build reliable accounting systems that scale. Our Houston-based team standardizes your chart of accounts, reconciles statements, and delivers financials you can trust.",
      "Contact Orsak CPA to streamline your close, improve cash flow visibility, and keep your records audit-ready year-round.",
    ],
  },
  {
    key: "audit",
    headline: "Schedule A Call About Audit Services",
    header: "Audits, Reviews, And Compilations You Can Rely On",
    description: [
      "Whether you need an audit for lenders, boards, or grants, we provide right-sized assurance—from compilations and reviews to full audits—aligned with professional standards.",
      "Reach out to Orsak CPA to scope the right level of assurance, tighten internal controls, and present financials with confidence.",
    ],
  },
  {
    key: "advisory",
    headline: "Let’s Map Your Next Strategic Move",
    header: "Forecasting, KPI Dashboards, And Fractional CFO Support",
    description: [
      "Get executive-level financial clarity without the full-time cost. We help Houston businesses with budgeting, scenario planning, pricing strategy, and board-ready reporting.",
      "Contact Orsak CPA to implement practical KPIs, strengthen cash management, and make data-driven decisions with confidence.",
    ],
  },
  {
    key: "tax",
    headline: "Get Proactive About Taxes",
    header: "Planning, Preparation, And Year-Round Strategy",
    description: [
      "Minimize surprises with intentional, year-round tax planning. We handle federal and Texas filings for businesses, individuals, and nonprofits, while identifying credits and deductions that fit your situation.",
      "Speak with Orsak CPA to align entity structure, quarterly estimates, and long-term strategy so tax season feels predictable.",
    ],
  },
  {
    key: "business",
    headline: "Build A Stronger Business Foundation",
    header: "Entity Setup, Systems, And Ongoing Operations",
    description: [
      "From new formations to established operations, we help you choose the right entity, implement accounting systems, and formalize processes for reliable growth.",
      "Contact Orsak CPA to tighten workflows, improve controls, and create financial visibility that supports your next stage.",
    ],
  },
  {
    key: "individual",
    headline: "Personal Tax & Planning Support",
    header: "Clear Guidance For Families And Individuals",
    description: [
      "We help professionals, investors, and families with tax prep, planning, estimated payments, and multi-state considerations—tailored to your goals in Houston, Texas and beyond.",
      "Reach out to Orsak CPA for straightforward advice, timely filings, and strategies that protect your after-tax income.",
    ],
  },
];

const SERVICE_ITEMS: ContactUsSection[] = [
  {
    key: "accounting-system-selection-and-setup",
    headline: "Set Up Your Accounting System The Right Way",
    header: "Chart Of Accounts, Automations, And Reporting From Day One",
    description: [
      "Choosing and configuring the right accounting system is critical to keeping accurate books and building reliable reports. We help you select a platform like QuickBooks Online or an industry-specific solution, and then customize it for your workflows.",
      "Contact Orsak CPA in Houston, Texas to establish a clean chart of accounts, automate reconciliations, and create reporting dashboards that scale with your business.",
    ],
  },
  {
    key: "compilation-of-financial-statements",
    headline: "Compile Clear, Professional Financial Statements",
    header: "GAAP-Ready Reports Without Assurance",
    description: [
      "Compilations transform your internal records into financial statements that follow professional formatting and disclosure standards. While no assurance is provided, compilations are ideal for management reviews, lenders, and basic compliance needs.",
      "Reach out to Orsak CPA to ensure your statements are presented consistently and ready to support decision-making or third-party requests.",
    ],
  },
  {
    key: "financial-position-job-descriptions",
    headline: "Define Roles That Strengthen Your Financial Team",
    header: "Job Descriptions Tailored To Finance And Accounting",
    description: [
      "Clear responsibilities create accountability and efficiency. We help you draft or refine job descriptions for bookkeepers, controllers, CFOs, and other finance roles—ensuring alignment with industry best practices and your company’s needs.",
      "Connect with Orsak CPA to establish job descriptions that strengthen your team, improve workflows, and provide clarity as you grow.",
    ],
  },
  {
    key: "policies-and-procedures",
    headline: "Document Policies And Procedures That Work",
    header: "Controls, Consistency, And Compliance",
    description: [
      "Well-documented accounting policies and procedures reduce risk, improve internal controls, and ensure your team works consistently. We design and implement process manuals that fit your business size and industry.",
      "Let Orsak CPA help you formalize controls, create reliable checklists, and maintain compliance with evolving standards.",
    ],
  },
  {
    key: "preparation-of-financial-statements",
    headline: "Get Timely, Reliable Financial Statements",
    header: "Monthly, Quarterly, And Year-End Reporting",
    description: [
      "Preparation services ensure your balance sheet, income statement, and cash flow reports are accurate and consistent. We help businesses of all sizes maintain books that are ready for lenders, investors, and management reviews.",
      "Contact Orsak CPA to schedule regular preparation of financial statements and gain confidence in your numbers year-round.",
    ],
  },
  {
    key: "software-selection-and-implementation",
    headline: "Implement The Right Financial Software",
    header: "Smart Selection, Smooth Rollout, And Ongoing Support",
    description: [
      "Selecting and implementing financial software is about more than just features—it’s about integration, usability, and long-term scalability. We guide you through selection, setup, and team training for tools that enhance efficiency.",
      "Reach out to Orsak CPA to streamline your implementation and ensure your financial software is fully optimized for your business.",
    ],
  },
  {
    key: "quickbooks-services",
    headline: "Get QuickBooks Working For You",
    header: "Setup, Cleanup, Training, And Ongoing Support",
    description: [
      "Whether you're starting fresh or cleaning up a messy file, we help you configure QuickBooks Online or Desktop the right way and train your team to keep it accurate.",
      "Contact Orsak CPA in Houston, Texas to get your QuickBooks set up for clean books and reliable, decision-ready reporting.",
    ],
  },
  {
    key: "budgeting-and-forecasts",
    headline: "Plan Ahead With Accurate Budgets & Forecasts",
    header: "Financial Roadmaps That Guide Smarter Decisions",
    description: [
      "Strong budgets and forecasts give you visibility into the future, helping you anticipate cash needs and measure performance against targets. We work with leadership to build realistic, flexible financial models that keep you on track.",
      "Contact Orsak CPA in Houston, Texas to design budgets, rolling forecasts, and scenario planning that provide clarity and confidence in your next steps.",
    ],
  },
  {
    key: "cash-flow-management",
    headline: "Take Control Of Your Cash Flow",
    header: "Monitoring, Forecasting, And Planning For Stability",
    description: [
      "Cash flow is the lifeblood of any business. We help you track inflows and outflows, identify timing gaps, and implement strategies that keep operations running smoothly and reduce financial stress.",
      "Let Orsak CPA help you optimize working capital, strengthen reserves, and align cash flow with long-term growth goals.",
    ],
  },
  {
    key: "disclosures-and-budget-support",
    headline: "Ensure Proper Disclosures And Budget Backing",
    header: "Compliance And Transparency For Stakeholders",
    description: [
      "Disclosures and budget support provide stakeholders with clear, reliable information. We help draft required disclosures, validate budgets, and ensure alignment with GAAP, lending covenants, or grant requirements.",
      "Reach out to Orsak CPA to make sure your budgets and disclosures build trust with lenders, boards, and regulators.",
    ],
  },
  {
    key: "gaap-implementation",
    headline: "Implement GAAP Standards With Confidence",
    header: "Accurate Reporting That Meets Professional Standards",
    description: [
      "GAAP compliance ensures your financials are consistent, comparable, and credible. We assist with implementing accounting standards, from revenue recognition to lease accounting, tailored to your industry.",
      "Contact Orsak CPA to transition smoothly into GAAP reporting and strengthen the reliability of your financial statements.",
    ],
  },
  {
    key: "medicare-cost-reports",
    headline: "Simplify Medicare Cost Reporting",
    header: "Accurate Submissions For Compliance And Funding",
    description: [
      "Healthcare providers face complex requirements when filing Medicare cost reports. We help you prepare accurate, timely reports that comply with regulations and maximize reimbursements.",
      "Work with Orsak CPA to reduce audit risk, streamline reporting, and ensure your practice stays compliant while protecting revenue.",
    ],
  },
  {
    key: "succession-planning-and-wealth-transfer",
    headline: "Plan For Smooth Succession & Wealth Transfer",
    header: "Protecting Business Value And Family Legacy",
    description: [
      "Succession planning is about more than just tax—it’s about continuity and peace of mind. We help business owners and families design plans that preserve value, minimize tax impact, and transfer wealth efficiently.",
      "Talk with Orsak CPA to craft a transition strategy that safeguards your business, your family, and your long-term goals.",
    ],
  },
  {
    key: "supplemental-schedules",
    headline: "Get Organized With Supplemental Schedules",
    header: "Supporting Detail For Clearer Financial Reporting",
    description: [
      "Supplemental schedules provide the detailed breakdowns behind your financial statements—giving stakeholders greater transparency and understanding. We prepare schedules tailored to lender, board, or regulatory needs.",
      "Connect with Orsak CPA to ensure your supplemental schedules are accurate, consistent, and support your overall reporting package.",
    ],
  },
  {
    key: "audit-of-financial-statements",
    headline: "Schedule Your Financial Statement Audit",
    header: "Independent Assurance For Lenders, Boards, And Grants",
    description: [
      "When stakeholders need confidence, an independent audit provides it. We plan and perform audits in accordance with professional standards, assess internal controls, and deliver clear, timely reports tailored to your organization’s risks.",
      "Contact Orsak CPA in Houston, Texas to scope the right timeline, coordinate PBC requests, and complete an efficient audit that stands up to scrutiny.",
    ],
  },
  {
    key: "audits-reviews-and-financial-statements",
    headline: "Right-Sized Assurance For Your Needs",
    header: "Audits, Reviews, And Compilations You Can Rely On",
    description: [
      "Whether you need an audit, review, or compilation, we match the level of assurance to your objectives and stakeholder requirements. Expect clear communication, practical requests, and deliverables that meet professional standards.",
      "Reach out to Orsak CPA to determine the appropriate engagement and timeline so you can present financials with confidence.",
    ],
  },
  {
    key: "internal-control-documentation",
    headline: "Document Controls That Reduce Risk",
    header: "Policies, Procedures, And Control Narratives",
    description: [
      "Strong internal control documentation clarifies responsibilities, supports compliance, and reduces the risk of error or fraud. We create practical policies, process maps, and narratives aligned to your workflows.",
      "Connect with Orsak CPA to formalize controls, improve audit readiness, and give leadership reliable visibility into how the numbers are produced.",
    ],
  },
  {
    key: "internal-controls",
    headline: "Design And Improve Internal Controls",
    header: "Segregation Of Duties, Approvals, And Monitoring",
    description: [
      "We evaluate your current processes, identify control gaps, and implement right-sized safeguards around cash, purchasing, revenue, payroll, and financial reporting. The result is better data, fewer surprises, and smoother audits.",
      "Contact Orsak CPA in Houston, Texas to strengthen controls without slowing down your team—so your books stay accurate year-round.",
    ],
  },
  {
    key: "review-of-financial-statements",
    headline: "Need A Financial Statement Review?",
    header: "Limited Assurance With Professional Presentation",
    description: [
      "A review engagement provides limited assurance through inquiries and analytical procedures—often sufficient for lenders or boards when a full audit isn’t required. We deliver timely reports with clear, consistent disclosures.",
      "Let’s discuss whether a review fits your situation and how Orsak CPA can complete it on a schedule that works for your team.",
    ],
  },
  {
    key: "special-purpose-financial-statements",
    headline: "Prepare Special-Purpose Financial Statements",
    header: "OCBOA, Tax-Basis, Cash-Basis, And Other Frameworks",
    description: [
      "Some audiences prefer statements prepared on alternative frameworks such as tax-basis, cash-basis, or other OCBOA. We tailor your reporting to the intended users while maintaining clarity and consistency.",
      "Reach out to Orsak CPA to choose the right framework, align disclosures, and deliver statements that meet your stakeholders’ needs.",
    ],
  },
  {
    key: "development-of-financial-policies-and-procedures",
    headline: "Develop Practical Financial Policies & Procedures",
    header: "Consistency, Compliance, And Stronger Controls",
    description: [
      "Clear, documented policies and procedures reduce errors, improve efficiency, and help your team stay compliant. We work with you to design processes that fit your operations while supporting internal controls and audit readiness.",
      "Contact Orsak CPA in Houston, Texas to formalize financial policies and procedures that protect your business and scale with growth.",
    ],
  },
  {
    key: "debt-covenant-compliance",
    headline: "Stay Ahead Of Debt Covenant Compliance",
    header: "Monitoring, Reporting, And Lender Confidence",
    description: [
      "Loan agreements often include financial covenants that require careful monitoring. We help track ratios, prepare compliance certificates, and communicate proactively with lenders to avoid surprises.",
      "Reach out to Orsak CPA to ensure you remain in good standing with your lenders while keeping financing flexible for future growth.",
    ],
  },
  {
    key: "development-of-budgets",
    headline: "Build Budgets That Drive Performance",
    header: "Strategic Planning And Measurable Targets",
    description: [
      "A well-designed budget aligns resources with priorities and provides benchmarks for accountability. We collaborate with management to develop budgets that are realistic, strategic, and easy to monitor.",
      "Let Orsak CPA support your budgeting process so you can make informed decisions, adjust quickly, and stay on track toward your goals.",
    ],
  },
  {
    key: "successful-planning",
    headline: "Plan For Long-Term Success",
    header: "Strategies For Growth, Stability, And Transitions",
    description: [
      "Successful planning means aligning financial, tax, and operational strategies with your vision. We help businesses and individuals in Houston, Texas prepare for growth, navigate challenges, and plan for the future.",
      "Connect with Orsak CPA to create a customized roadmap that balances today’s needs with tomorrow’s opportunities.",
    ],
  },
  {
    key: "part-time-controller-cfo",
    headline: "Get Fractional Controller & CFO Support",
    header: "Senior-Level Guidance Without A Full-Time Hire",
    description: [
      "Access experienced financial leadership on a part-time basis. We provide budgeting, forecasting, KPI dashboards, and board-ready reporting, along with oversight of accounting teams and internal controls.",
      "Contact Orsak CPA to gain the insights of a controller or CFO—tailored to your needs—without the overhead of a full-time executive.",
    ],
  },
  {
    key: "individual-accounting",
    headline: "Streamline Your Personal Books",
    header: "Individual Accounting, Rentals, And Investments",
    description: [
      "Keep your personal finances organized with clear categorization, reconciled accounts, and ready-to-file records. We help with rental property schedules, brokerage imports, and estimated tax planning—so you always know where you stand.",
      "Contact Orsak CPA in Houston, Texas for individual accounting support tailored to professionals, investors, and families, including multi-state considerations and coordination with trusts or LLCs.",
    ],
  },
  {
    key: "personal-financial-statements",
    headline: "Present Lender-Ready Personal Financial Statements",
    header: "Net Worth, Liquidity, And Supporting Schedules",
    description: [
      "Whether for lending, surety, or planning, we prepare personal financial statements that clearly present assets, liabilities, and liquidity. Expect accurate valuations, organized schedules, and documentation that stands up to review.",
      "Reach out to Orsak CPA to compile a professional, consistent personal financial statement package—so you can move quickly on loans and opportunities.",
    ],
  },
  {
    key: "estate-and-trusts",
    headline: "Estate & Trust Taxes Made Clear",
    header: "Fiduciary Accounting, 1041 Filings, And Distributions",
    description: [
      "Estate and trust administration requires careful coordination of accounting, tax filings, and beneficiary distributions. We prepare fiduciary returns (Form 1041), track income vs. corpus, and help executors and trustees stay compliant.",
      "Contact Orsak CPA in Houston, Texas to streamline estate and trust reporting, minimize tax impact where possible, and keep beneficiaries informed with clean schedules.",
    ],
  },
  {
    key: "federal-tax-return-preparation",
    headline: "File Accurate Federal Tax Returns",
    header: "Businesses, Individuals, And Nonprofits",
    description: [
      "We prepare and review federal income tax returns with organized workpapers, clear communication, and proactive issue-spotting. Expect efficient document collection, e-sign options, and returns that reflect your real activity.",
      "Reach out to Orsak CPA to handle your Form 1120/1120S/1065/1040/990 filings on time—and reduce notices through clean, consistent reporting.",
    ],
  },
  {
    key: "tax-planning",
    headline: "Proactive, Year-Round Tax Planning",
    header: "Entity Structure, Timing, Credits, And Elections",
    description: [
      "Taxes shouldn’t be a once-a-year surprise. We model scenarios, optimize entity structure, and plan timing of income and deductions to keep cash predictable and after-tax income higher.",
      "Talk with Orsak CPA about quarterly estimates, safe harbors, and long-term strategies tailored to your goals in Houston, Texas.",
    ],
  },
  {
    key: "state-franchise-income-and-tax-return",
    headline: "State Franchise & Income Tax Returns",
    header: "Registrations, Nexus, And Multi-State Compliance",
    description: [
      "As your footprint grows, so do state filing requirements. We evaluate nexus, register where needed, and prepare franchise and income tax returns that align with your operations and apportionment.",
      "Connect with Orsak CPA to keep state filings current, reduce audit risk, and avoid penalties with a clear compliance calendar.",
    ],
  },
  {
    key: "tax-compliance",
    headline: "Stay Current On All Tax Filings",
    header: "Federal, State, Payroll, And Information Returns",
    description: [
      "From federal and state income tax to payroll and information reporting, we manage the details so deadlines don’t derail your plans. Our process emphasizes organized requests, timely filing, and fast responses to notices.",
      "Contact Orsak CPA to centralize compliance, track due dates, and maintain clean records that make every year easier than the last.",
    ],
  },
  {
    key: "oil-and-gas-royalty-tax",
    headline: "Talk With a CPA Who Knows Royalty Income",
    header: "Depletion, Interest Types, and Multi-State Filings",
    description: [
      "Mineral and royalty income comes with depletion deductions, working-versus-royalty distinctions, and K-1s and 1099s that are easy to misreport. We help Texas owners file accurately and capture every deduction the rules allow.",
      "Contact Orsak CPA in Houston, Texas to coordinate your royalty income with your broader tax and estate plan—so more of what your minerals earn stays with you.",
    ],
  },
  {
    key: "ranch-and-landowner-tax",
    headline: "Plan the Tax Side of Your Texas Land",
    header: "Ag Valuation, Mineral Income, and Family Transfer",
    description: [
      "Texas land touches agricultural and wildlife valuation, rollback exposure, mineral income, and long-term family transfer. We help ranchers, farmers, and landowners keep valuations intact and plan transfers that minimize tax.",
      "Contact Orsak CPA to align your land, livestock, and mineral interests with a tax strategy built for the long haul.",
    ],
  },
  {
    key: "real-estate-investor-tax",
    headline: "Build a Real Estate Tax Strategy That Compounds",
    header: "1031 Exchanges, Cost Segregation, and REP Status",
    description: [
      "Whether you hold rentals, invest in syndications, or flip properties, the right tax strategy can dramatically improve your after-tax return. We coordinate 1031 exchanges, cost segregation, and real estate professional status with clean reporting.",
      "Contact Orsak CPA in Houston, Texas to structure your portfolio for lower taxes and stronger long-term growth.",
    ],
  },
  {
    key: "business-sale-exit",
    headline: "Start Exit Planning Before the Offer",
    header: "QSBS, Installment Sales, and Deal Structuring",
    description: [
      "How a sale is structured can change your after-tax proceeds by a wide margin. We plan QSBS eligibility, installment sales, and asset-versus-stock structuring well before the letter of intent, then support you through diligence.",
      "Contact Orsak CPA to prepare your financials and tax position so your exit delivers the most value possible.",
    ],
  },
];

const INDUSTRIES: ContactUsSection[] = [
  {
    key: "manufacturing",
    headline: "Talk With A CPA Who Understands Manufacturing",
    header: "Cost Accounting, Inventory, And Margin Control",
    description: [
      "From job costing to inventory valuation and production variance analysis, manufacturers face unique accounting and tax challenges. Orsak CPA helps Houston, Texas manufacturers build reliable cost systems, strengthen controls, and improve unit economics.",
      "Get in touch to align your bookkeeping, tax planning, and CPA oversight with your production realities—so you can price confidently, protect margins, and scale with clarity.",
    ],
  },
  {
    key: "construction",
    headline: "Let’s Plan Your Next Build—Financially",
    header: "WIP Schedules, Job Costing, And Contractor Compliance",
    description: [
      "Contractors live and die by accurate job costing, WIP reporting, and cash flow forecasting. We help GCs and specialty trades implement systems for progress billing, retainage, and certified payroll while staying compliant with Texas and federal requirements.",
      "Contact Orsak CPA to tighten controls, improve project visibility, and reduce tax surprises—so every project finishes stronger.",
    ],
  },
  {
    key: "real-estate",
    headline: "Real Estate Accounting Made Clear",
    header: "Entity Structure, Depreciation, And 1031 Strategy",
    description: [
      "Whether you’re managing rentals, syndications, or development, real estate demands precise books and proactive tax strategy. We help Houston investors with entity selection, depreciation schedules, cost segregation coordination, and clean investor reporting.",
      "Reach out to Orsak CPA to streamline books, optimize tax outcomes, and present investor-ready financials with confidence.",
    ],
  },
  {
    key: "technology",
    headline: "Finance Ops For Modern Tech Teams",
    header: "SaaS Metrics, R&D Credits, And Clean Close Processes",
    description: [
      "From capitalizing software development to recognizing revenue and tracking KPIs like ARR, MRR, CAC, and LTV, tech companies need tight financial operations. We help startups and established teams in Houston, Texas build scalable reporting and tax strategies.",
      "Contact us to implement disciplined close processes, support fundraising diligence, and leverage incentives like R&D credits where appropriate.",
    ],
  },
  {
    key: "transportation",
    headline: "Keep Your Fleet And Finances Moving",
    header: "IFTA, Depreciation, And Cash Flow Planning",
    description: [
      "Transport and logistics firms juggle fuel taxes, equipment financing, and razor-thin margins. Orsak CPA helps with IFTA reporting, equipment depreciation, bookkeeping that separates lanes and routes, and tax planning that supports growth.",
      "Let’s talk about tightening your books, improving forecasting, and staying audit-ready while you focus on on-time deliveries.",
    ],
  },
  {
    key: "oil-and-gas",
    headline: "CPA Support For Oil & Gas Operations",
    header: "Lease Accounting, Depletion, And JV Reporting",
    description: [
      "From upstream accounting and depletion to joint-interest billing and royalty statements, oil & gas requires industry-specific expertise. We support Houston operators, service companies, and investors with reliable books and proactive tax planning.",
      "Connect with Orsak CPA to standardize reporting, maintain compliance, and gain financial visibility across wells, leases, and partnerships.",
    ],
  },
  {
    key: "healthcare",
    headline: "Financial Clarity For Healthcare Practices",
    header: "Revenue Cycles, Compliance, And Practice Growth",
    description: [
      "Medical, dental, and allied practices need clean separation of gross charges, adjustments, and collections—plus airtight compliance. We align your practice management and accounting systems for accurate, audit-ready reporting and cash flow insight.",
      "Reach out to Orsak CPA for help with budgeting, tax planning, and benchmarking so your practice stays healthy and growing.",
    ],
  },
  {
    key: "ranching-and-agribusiness",
    headline: "Grow A Stronger Operation, Season After Season",
    header: "Inventory, Biological Assets, And Ag-Specific Tax Planning",
    description: [
      "Agribusiness demands careful tracking of inputs, inventory, and multi-season cash flows. We help Texas ranchers and producers keep clear books, plan depreciation, and navigate ag-specific deductions and credits.",
      "Contact Orsak CPA to stabilize finances across seasons, simplify reporting, and make tax time predictable.",
    ],
  },
  {
    key: "professional",
    headline: "Service Firms Deserve Service-Grade Accounting",
    header: "Utilization, Pricing, And Predictable Cash Flow",
    description: [
      "Law, architecture, marketing, consulting—service firms hinge on clean time tracking, WIP management, and effective pricing. We implement systems that clarify utilization, improve billing, and keep partners informed.",
      "Let’s tailor bookkeeping, forecasting, and tax strategy so your Houston firm can scale without sacrificing quality.",
    ],
  },
  {
    key: "nonprofits",
    headline: "Strengthen Mission With Sound Financials",
    header: "Fund Accounting, Grants, And Board-Ready Reports",
    description: [
      "Nonprofits need transparent fund accounting, clean grant tracking, and GAAP-compliant statements. Orsak CPA helps Houston nonprofits maintain donor trust with accurate books, clear reporting, and confident audits or reviews.",
      "Get in touch to streamline compliance, strengthen internal controls, and present board-ready financials that support your mission.",
    ],
  },
];

const LOCATIONS: ContactUsSection[] = [
  {
    key: "locations",
    headline: "Connect With Orsak CPA’s Today",
    header: "Let's Build Your Financial Confidence",
    description: [
      "Whether you need ongoing accounting support, help preparing for an audit, or strategic tax planning, our team is here to help.",
      "Use the form below or reach out directly by phone or email, and one of our CPAs will respond promptly to schedule a consultation.",
      "We proudly serve clients across Houston, Texas, and the Gulf Coast Region—delivering clarity, compliance, and confidence with every engagement.",
    ],
  },
];

export const CONTACT_US_SECTIONS: ContactUsSection[] = [
  {
    key: "",
    headline: "Get In Touch With Orsak CPAs",
    header: "Your Trusted CPA Firm In Houston, Texas",
    description: [
      "Whether you're an individual, business owner, or nonprofit leader, we're here to provide the accounting, tax, and advisory support you need. Our Houston-based team has decades of combined experience helping clients across Texas reach their financial goals with clarity and confidence.",
      "Contact us today to schedule a free first consultation and discover how Orsak CPAs can simplify your accounting challenges and provide proactive solutions that set you up for success.",
    ],
  },
  {
    key: "services",
    headline: "Contact Us About Our Services",
    header: "Expert Guidance For Every Service We Provide",
    description: [
      "From tax planning to audits, accounting systems, and advisory support, our team of professionals is ready to assist with every financial service your organization may need. We'll walk you through your options, answer your questions, and help you decide on the best next steps for your unique situation.",
      "Reach out today to discuss our full range of services and learn how Orsak CPAs can tailor a solution that meets your exact goals.",
    ],
  },
  {
    key: "industries",
    headline: "Let's Talk About Your Industry",
    header: "Customized CPA Solutions For Houston's Key Industries",
    description: [
      "Every industry faces its own accounting, compliance, and tax challenges. Whether you work in construction, healthcare, oil & gas, or professional services, Orsak CPAs understands the details that matter. Our expertise ensures you'll have strategies that fit your field and keep you on track.",
      "Contact us to learn how we support businesses and nonprofits across Houston, Texas, and discover what we can do for your industry.",
    ],
  },
  {
    key: "contact-us",
    headline: "Talk With a CPA Today",
    header: "Contact Orsak CPAs in Houston, Texas",
    description: [
      "Tell us what you’re working on and where you need help. We’ll connect you with the right CPA and outline clear next steps.",
      "Most inquiries receive a same-business-day reply with options to schedule a call, share documents, or request a proposal.",
    ],
  },
  {
    key: "about",
    headline: "Get To Know Our Firm",
    header: "A Houston CPA Partner Invested In Your Success",
    description: [
      "Orsak CPAs combines big-firm expertise with the personal attention of a local Houston practice. We take time to understand your goals before recommending solutions.",
      "Reach out to start a conversation and see why businesses and individuals across Texas trust Orsak Langner & Barthel PLLC.",
    ],
  },
  {
    key: "irs-resolution",
    headline: "Facing An IRS Problem? Let's Talk",
    header: "Experienced IRS Representation In Houston, Texas",
    description: [
      "Back taxes, liens, levies, garnishment, or unfiled returns—IRS problems rarely improve on their own. We help you understand your options and deal with the IRS on your behalf.",
      "Contact Orsak CPAs for a confidential consultation about resolving your tax matter and getting back on solid ground.",
    ],
  },
  ...SERVICE_CATEGORIES,
  ...SERVICE_ITEMS,
  ...INDUSTRIES,
  ...LOCATIONS,
];

export const CONTACT_US_SECTION_BY_KEY = Object.fromEntries(
  CONTACT_US_SECTIONS.map(section => [section.key, section]),
);
